import TapeFileReview from './TapeFileReview';
import { isUpcomingPeriod, useReviewPeriodLock, useReviewPeriodLocked, periodQuery } from '../workspace/reviewPeriod';
import { WorkspaceGuide, WorkspaceViews } from '../components/WorkspaceExperience';
import { useSearchParams, Link } from 'react-router-dom';
import { useAbfCopilotContext } from '../copilot/AbfCopilotLayout';
import { facilityWorkspacePath } from '../routes';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { isValidAmountShorthand, parseAmountShorthand, replaceShorthandOnBlur } from '../../../utils/amountUtils';
import {
  AlertTriangle, Check, CheckCircle2, CircleDot, FileUp, Keyboard,
  RefreshCw, ShieldCheck, Upload,
} from 'lucide-react';
import TapeCorrectionPanel from './TapeCorrectionPanel';
import ComparisonCard from './ComparisonCard';
import BridgeCard from './BridgeCard';
import { apiErrorMessage } from '../api/abfApi';
import { StatusChip } from '../components/StatusChip';
import { type ActionFeedbackState } from '../components/ActionFeedback';
import { Layout, Note, Readable, Signoff, SmallButton, TaskButton, TaskRail, Work } from '../onboarding/review/contractReviewStyles';
import { Button, Card, CardHead, FieldLabel, INK, Input } from '../onboarding/review/reviewStyles';
import {
  preparePeriodRecomputation, reparsePeriodTape, getComparisonRecord, decidePeriodFraud, GRID_COLUMNS, addKeyedCertificate, addPeriodTape, approveMapping, proposeMapping, correctTapeRow, decideBorrowingBase,
  decideRowExclusion, getPeriodWorkspace, getTapeRows, gridToCsv, proposeRowExclusion,
  reconcilePeriod, releasePeriodTape, discardPeriodTape,
  type AttentionItem, type CollectionPeriod, type MappingReview, type PeriodMetric, type PeriodWorkspace as Workspace, type RowIssue,
} from './periodApi';
import { DateField } from '../../../components/shared/DateField';
import { useBusinessDate } from '../../../contexts/BusinessDateContext';
import { fmtDate, fmtMoney, fmtNumber, plural } from '../presentation/format';

/**
 * The monthly collateral cycle, one period at a time, on the contract review
 * standard (approved 2026-09-11): a rail of periods, the selected period's
 * evidence beside what it means, only what needs attention opened, and one
 * next action. Validation, the base and the comparison run underneath; release
 * and approval stay human (FRD §9.4).
 */

interface Props {
  facilityId: number;
  currency?: string | null;
  userRole?: string | null;
  onOpenExplorer?: (tapeId: number) => void;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}


const Shell = styled.div`
  color: ${INK.body};
  font-size: 14px;
  line-height: 1.55;
  h2, h3, p { margin: 0; }
  h2 { font-size: 17px; }
  h3 { font-size: 15px; }
  button, input, select, textarea { font-family: inherit; }
`;



const StageLine = styled.ol`
  display: flex; gap: 4px; list-style: none; padding: 0; margin: 6px 0 0;
  li { width: 14px; height: 6px; border-radius: 3px; background: #26364a; }
  li[data-state='DONE'] { background: #4fd1a5; }
  li[data-state='OPEN'] { background: ${INK.accent}; }
  li[data-state='BLOCKED'] { background: #f0a24a; }
`;

const Strip = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; margin-bottom: 16px;
  > div { padding: 12px 14px; border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.panel}; }
  span { display: block; color: ${INK.faint}; font-size: 11px; font-weight: 750; letter-spacing: .08em; text-transform: uppercase; }
  strong { display: block; margin-top: 4px; font-size: 18px; letter-spacing: -.01em; color: ${INK.strong}; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 3px; }
`;

const Grid2 = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; align-items: start;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;

const Facts = styled.dl`
  display: grid; grid-template-columns: max-content 1fr; gap: 6px 14px; margin: 0;
  dt { color: ${INK.muted}; font-size: 13px; }
  dd { margin: 0; color: ${INK.body}; font-size: 13px; overflow-wrap: anywhere; }
`;

const DropZone = styled.label<{ $active?: boolean }>`
  display: grid; place-items: center; gap: 6px; padding: 26px 16px; text-align: center; cursor: pointer;
  border: 1.5px dashed ${p => p.$active ? INK.accent : INK.lineStrong}; border-radius: 12px;
  background: ${p => p.$active ? 'rgba(56,189,248,.08)' : INK.raised}; color: ${INK.soft};
  strong { color: ${INK.strong}; }
  small { color: ${INK.faint}; }
  position: relative;
  &:focus-within { outline: 2px solid ${INK.accent}; outline-offset: 3px; }
  input { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
  input:disabled { cursor: not-allowed; }
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid ${INK.line}; vertical-align: top; }
  th { color: ${INK.faint}; font-size: 11px; font-weight: 750; letter-spacing: .06em; text-transform: uppercase; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr[data-blocking='true'] td:first-child { border-left: 3px solid #f0a24a; }
  tr[data-accepted='true'] td { color: ${INK.muted}; }
  tbody tr:hover { background: rgba(56,189,248,.04); }
`;

const GridTable = styled.div`
  overflow-x: auto; border: 1px solid ${INK.line}; border-radius: 10px;
  table { min-width: 1200px; }
  input { width: 100%; min-width: 90px; box-sizing: border-box; padding: 6px 8px; border: 1px solid ${INK.lineStrong}; border-radius: 6px; background: ${INK.well}; color: ${INK.body}; font-size: 13px; }
`;

const Bar = styled.div`
  position: sticky; bottom: 0; z-index: 5; display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  gap: 12px; margin-top: 18px; padding: 12px 14px; border: 1px solid ${INK.line}; border-radius: 10px;
  background: rgba(9,18,31,.96); backdrop-filter: blur(4px);
  .state { color: ${INK.muted}; font-size: 13px; } .state strong { color: ${INK.strong}; }
  .actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  textarea { min-width: 260px; min-height: 40px; padding: 8px 10px; border: 1px solid ${INK.lineStrong}; border-radius: 8px; background: ${INK.well}; color: ${INK.body}; font-size: 13px; }
`;

const Attention = styled.article<{ $severity: string }>`
  padding: 12px 14px; border: 1px solid ${p => p.$severity === 'HIGH' ? 'rgba(240,162,74,.5)' : INK.line};
  border-radius: 10px; background: ${INK.panel}; margin-top: 10px;
  .head { display: flex; gap: 10px; align-items: flex-start; justify-content: space-between; }
  .head strong { color: ${INK.strong}; }
  .head p { color: ${INK.muted}; font-size: 13px; margin-top: 3px; }
`;

const Gates = styled.ul`
  list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 6px 14px;
  li { display: flex; gap: 8px; align-items: flex-start; font-size: 13px; color: ${INK.soft}; }
  li[data-ok='false'] { color: #f0c77a; }
  li small { display: block; color: ${INK.faint}; }
`;

// One money, number and date style across the ABF tabs (presentation/format.ts); these only fix the fallback currency.
const money = (v: number | null | undefined, currency?: string | null) => fmtMoney(v, currency || 'USD');
const num = fmtNumber;
const date = (v: string | null | undefined) => fmtDate(v);
const who = (v: string | null | undefined) => v || '—';

/**
 * What the rail and the bottom bar say is next. When nothing needs a maker and the only
 * thing open is an approval gate, say so — "Resolve the open items" otherwise contradicts
 * step 2's "Nothing outstanding".
 */
const railNextAction = (p: CollectionPeriod) => {
  const r = p.readiness;
  // Only once the evidence is in: before that the maker's next step is to add it.
  if (r.phase === 'IN_PROGRESS' && !p.attention.length && r.blockers.length > 0 && !r.canApprove) {
    return `${plural(r.blockers.length, 'approval gate')} open`;
  }
  return r.nextAction.label;
};

const stageOrder: Array<keyof CollectionPeriod['stages']> = ['received', 'validated', 'released', 'computed', 'certificate', 'compared', 'approved'];
const stageLabel: Record<string, string> = {
  received: 'Tape received', validated: 'Validated', released: 'Released', computed: 'Base computed',
  certificate: 'Certificate', compared: 'Compared', approved: 'Approved',
};

const phaseChip = (phase: CollectionPeriod['readiness']['phase']) => phase === 'APPROVED'
  ? <StatusChip tone="done" label="Approved" size="md" />
  : phase === 'AWAITING_CHECKER' ? <StatusChip tone="progress" label="Ready for checker" size="md" />
    : phase === 'IN_PROGRESS' ? <StatusChip tone="warning" label="In progress" size="md" />
      : <StatusChip tone="neutral" label="Awaiting evidence" size="md" />;

const PeriodWorkspace: React.FC<Props> = ({ facilityId, currency, userRole, onFeedback, onOpenExplorer }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { businessDate } = useBusinessDate();
  const periodChangeLocked = useReviewPeriodLocked();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(() => Number(searchParams.get('period') || searchParams.get('periodId')) || null);
  useEffect(() => { setSelectedId(Number(searchParams.get('period') || searchParams.get('periodId')) || null); }, [searchParams]);
  const workOnTapeId = Number(searchParams.get('tape')) || null;
  useAbfCopilotContext({periodId: selectedId || undefined, tapeId: workOnTapeId || undefined, subview: 'period'});
  const [busy, setBusy] = useState<string | null>(null);
  useReviewPeriodLock(busy !== null);
  const alive = useRef(true);
  const loadSequence = useRef(0);
  const workRef = useRef<HTMLElement>(null);

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async (quiet = false) => {
    const sequence = ++loadSequence.current;
    if (!quiet) setLoading(true);
    try {
      const next = await getPeriodWorkspace(facilityId, workOnTapeId);
      if (!alive.current || sequence !== loadSequence.current) return;
      setWorkspace(next); setError(null);
    } catch (failure) {
      if (alive.current && sequence === loadSequence.current) setError(apiErrorMessage(failure, 'The collateral cycle could not be loaded.'));
    } finally { if (alive.current && sequence === loadSequence.current) setLoading(false); }
  }, [facilityId, workOnTapeId]);
  useEffect(() => { void load(); }, [load]);

  const periods = useMemo(() => workspace?.periods ?? [], [workspace]);
  // Default to the period that needs a person: the earliest unapproved one.
  const selected = useMemo(() => {
    if (!periods.length) return null;
    const byId = periods.find(p => p.id === selectedId);
    if (byId) return byId;
    const open = [...periods].reverse().find(p => p.readiness.phase !== 'APPROVED');
    return open || periods[0];
  }, [periods, selectedId]);

  const run = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try {
      await action();
      onFeedback({ tone: 'success', message: success });
      await load(true);
      return true;
    } catch (failure) {
      onFeedback({ tone: 'error', message: apiErrorMessage(failure, 'The action was refused.') });
      await load(true);
      return false;
    } finally { setBusy(null); }
  };

  if (!workspace) return <Shell><Note role={error ? 'alert' : 'status'}>
    <p>{error || 'Loading the collateral cycle…'}</p>
    {error && <SmallButton type="button" onClick={() => void load()} disabled={loading}>Retry</SmallButton>}
  </Note></Shell>;

  const availability = workspace.availability;

  return <Shell>
    {error && <Note role="alert" style={{ marginBottom: 12 }}>{error} Displayed information may be out of date.</Note>}

    <Strip aria-label="Collateral authority">
      <div><span>Approved borrowing base</span><strong>{money(availability.approvedBase, currency)}</strong>
        <small>{availability.authority ? `Period ${availability.authority.periodLabel || '—'} · approved by ${availability.authority.approvedBy}` : 'No period approved yet'}</small></div>
      <div><span>Drawable now</span><strong>{money(availability.drawable, currency)}</strong>
        <small>{availability.reason || `after ${money(availability.drawn, currency)} drawn and ${money(availability.reserved, currency)} reserved`}</small></div>
      <div><span>Periods</span><strong>{periods.filter(p => p.readiness.phase === 'APPROVED').length} of {periods.length} approved</strong>
        <small>{(() => { const n = periods.filter(p => p.attention.length).length; return n === 1 ? '1 needs attention' : `${n} need attention`; })()}</small></div>
    </Strip>

    {workspace.calendarMissing && <Note style={{ marginBottom: 14 }}>
      <strong>No collection periods yet.</strong>
      <p>Periods come from the payment calendar on the contract. Enter it in contract review and the periods appear here with their due dates.</p>
    </Note>}

    {workspace.unattached.length > 0 && <Note style={{ marginBottom: 14 }}>
      <strong>{workspace.unattached.length} {workspace.unattached.length === 1 ? 'delivery has' : 'deliveries have'} no matching period</strong>
      {workspace.unattached.map(u => <p key={`${u.kind}-${u.id}`}>{u.kind === 'TAPE' ? 'Tape' : 'Certificate'} as of {date(String(u.as_of_date))} — {u.reason}</p>)}
    </Note>}

    <Layout>
      <TaskRail aria-label="Collection periods">
        <div className="rail-heading"><h2>Periods</h2><p>Select a period to work it</p></div>
        <div className="tasks">
          {(() => {
            // Current and past periods newest first; months that have not ended yet are grouped
            // under "Upcoming" at the bottom (the Collections rail pattern), so the period that
            // needs a person is the first thing in view.
            const today = businessDate || new Date().toISOString().slice(0, 10);
            const notEnded = (p: CollectionPeriod) => isUpcomingPeriod(p.periodStart, today);
            const byNewest = (a: CollectionPeriod, b: CollectionPeriod) => String(b.periodStart).localeCompare(String(a.periodStart));
            const railLine = (p: CollectionPeriod) => {
              if (p.readiness.phase === 'APPROVED') return p.readiness.nextAction.label;
              if (notEnded(p)) return `Not started · tape due ${fmtDate(p.deliverablesDueDate)}`;
              return railNextAction(p);
            };
            const renderChip = (p: CollectionPeriod) => <TaskButton key={p.id} type="button" disabled={periodChangeLocked} $active={selected?.id === p.id} aria-pressed={selected?.id === p.id}
              onClick={() => { setSelectedId(p.id); const next = periodQuery(searchParams, p.id); setSearchParams(next, { replace: true }); if (window.matchMedia('(max-width: 1000px)').matches) workRef.current?.scrollIntoView({ block: 'start' }); }}>
              <div className="category">{date(p.periodStart)} – {date(p.periodEnd)}</div>
              <strong>{p.label}</strong>
              <StageLine aria-label="Progress">{stageOrder.map(s => <li key={s} data-state={p.stages[s].state} title={`${stageLabel[s]}: ${p.stages[s].detail}`} />)}</StageLine>
              <span className="reason" style={{ color: p.readiness.phase === 'APPROVED' ? '#7cddbe' : notEnded(p) ? INK.muted : undefined }}>
                {p.readiness.phase === 'APPROVED' ? <Check size={12} /> : <CircleDot size={12} />}{railLine(p)}
              </span>
              {p.attention.length > 0 && <span className="owner">{p.attention.length} need{p.attention.length === 1 ? 's' : ''} attention</span>}
            </TaskButton>;
            const current = periods.filter(p => !notEnded(p)).sort(byNewest);
            const later = periods.filter(notEnded).sort((a, b) => String(a.periodStart).localeCompare(String(b.periodStart)));
            return <>
              {current.map(renderChip)}
              {later.length > 0 && <div className="group-heading">Upcoming<span>{later.length}</span></div>}
              {later.map(renderChip)}
            </>;
          })()}
          {!periods.length && <p className="empty">No periods on the calendar.</p>}
        </div>
      </TaskRail>

      <Work ref={workRef} aria-label="Selected period">
        {selected && <PeriodPane key={`${facilityId}-${selected.id}`} period={selected} facilityId={facilityId} currency={currency}
          onOpenExplorer={onOpenExplorer} userRole={userRole} mappingReviews={workspace.mappingReviews} busy={busy} run={run} reload={() => load(true)}
          onWorkOnTape={tapeId => { const copy = new URLSearchParams(searchParams); if (tapeId) copy.set('tape', String(tapeId)); else copy.delete('tape'); setSearchParams(copy, { replace: true }); }} />}
      </Work>
    </Layout>
  </Shell>;
};

// ----------------------------------------------------------------------------

interface PaneProps {
  period: CollectionPeriod; facilityId: number; currency?: string | null;
  mappingReviews: Workspace['mappingReviews']; userRole?: string | null; onOpenExplorer?: (tapeId: number) => void;
  busy: string | null; run: (key: string, action: () => Promise<unknown>, success: string) => Promise<boolean>;
  reload: () => Promise<void>;
  onWorkOnTape?: (tapeId: number | null) => void;
}

const PeriodPane: React.FC<PaneProps> = ({ period, facilityId, currency, mappingReviews, busy, run, onOpenExplorer, onWorkOnTape }) => {
  const r = period.readiness;
  const approved = r.phase === 'APPROVED';
  const [notes, setNotes] = useState('');
  const [reviewView, setReviewView] = useState(approved || r.phase === 'AWAITING_CHECKER' ? 'approval' : period.attention.length ? 'issues' : 'evidence');
  const [historyOpen, setHistoryOpen] = useState(false);
  const explorerTape = period.history.tapes.find(t => t.released_at);

  const evidence = <Grid2>
    <TapeCard period={period} facilityId={facilityId} currency={currency} busy={busy} run={run} />
    <CertificateCard period={period} facilityId={facilityId} currency={currency} busy={busy} run={run} />
  </Grid2>;


  return <>
    <div className="work-heading">
      <div>
        <h2>Period {period.label}</h2>
        <p>Deliverables due {date(period.deliverablesDueDate)} · determination {date(period.determinationDate)} · payment {date(period.paymentDate)}</p>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>{phaseChip(r.phase)}</div>
    </div>



    <WorkspaceGuide><h2>{approved ? 'Period approved' : railNextAction(period)}</h2><p>{approved ? 'The approved base is available to Funding. Inspect the evidence and comparison below or open Collections to continue the monthly close.' : r.nextAction.reason || 'Work through the evidence, issues and comparison before the checker decision.'}</p></WorkspaceGuide>
    <WorkspaceViews label="Collateral period review steps" value={reviewView} items={[{key:'evidence',label:'1 · Receive evidence'},{key:'issues',label:`2 · Resolve issues (${period.attention.length})`},{key:'base',label:'3 · Explain the base'},{key:'comparison',label:'4 · Compare certificate'},{key:'approval',label:'5 · Review & approve'}]} onChange={setReviewView} />
    <div hidden={reviewView !== 'issues'}>
    {!period.attention.length && <Note>No open data issues for this period. Review the base and certificate comparison; approval gates remain explicit.</Note>}
    {period.attention.length > 0 && <AttentionList period={period} facilityId={facilityId} currency={currency} mappingReviews={mappingReviews} busy={busy} run={run} />}

    </div>
    <div hidden={reviewView !== 'evidence'}>{evidence}</div>
    <div hidden={reviewView !== 'base'}>{period.run ? <BridgeCard period={period} currency={currency} /> : <Note>The borrowing base is computed after the tape is released.</Note>}</div>
    <div hidden={reviewView !== 'comparison'}>{period.comparison ? <ComparisonCard period={period} facilityId={facilityId} currency={currency} busy={busy} run={run} /> : <Note>The comparison appears when the tape and certificate are available.</Note>}</div>
    <div hidden={reviewView !== 'approval'}>

    <Signoff open>
      <summary>Approval <small>{approved ? `Approved ${date(period.run?.approvedAt)} by ${who(period.run?.approvedBy)}` : r.canApprove ? 'Ready for your decision' : `${plural(r.blockers.length, 'gate')} open`}</small></summary>
      <Readable>
        <Gates aria-label="Approval gates">
          {r.gates.map(g => <li key={g.key} data-ok={g.satisfied}>{g.satisfied ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}<div>{g.label}{!g.satisfied && g.fix && <small>{g.fix}</small>}</div></li>)}
        </Gates>
        <p style={{ marginTop: 12, color: INK.muted, fontSize: 13 }}>
          Prepared by {r.contributors.length ? r.contributors.join(', ') : '—'}. {r.actorPrepared ? 'You are among them, so a different person approves.' : 'Approval needs someone who did not prepare any part of this period.'}
        </p>
      </Readable>
    </Signoff>

    </div>
    {!approved && <Bar>
      <div className="state">
        <strong>{railNextAction(period)}.</strong> {railNextAction(period) !== r.nextAction.label ? 'Nothing needs a maker; the open gate is listed under Approval.' : r.nextAction.reason || (r.nextAction.owner === 'SYSTEM' ? 'Running automatically.' : r.nextAction.owner === 'CHECKER' ? 'A checker’s decision.' : 'A maker’s task.')}
      </div>
      {reviewView === 'approval' && r.nextAction.code === 'APPROVE' && <div className="actions">
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Your approval notes — what you checked, in your own words" aria-label="Approval notes" />
        <Button type="button" onClick={() => run('reject', () => decideBorrowingBase(facilityId, period.run!.id, 'REJECT', notes), 'Borrowing base rejected')} disabled={!r.canApprove || !notes.trim() || busy !== null}>Reject</Button>
        <Button type="button" $variant="primary" onClick={() => run('approve', () => decideBorrowingBase(facilityId, period.run!.id, 'APPROVE', notes), 'Borrowing base approved and published to funding')} disabled={!r.canApprove || !notes.trim() || busy !== null}>
          {busy === 'approve' ? 'Approving…' : 'Approve borrowing base'}
        </Button>
      </div>}
      {period.attention.some(item => item.action === 'RETRY_PROCESSING') && <SmallButton type="button" onClick={() => run('reconcile', () => reconcilePeriod(facilityId, period.id), 'Period reconciled')} disabled={busy !== null}><RefreshCw size={14} /> Retry comparison</SmallButton>}
    </Bar>}

    {explorerTape && onOpenExplorer && <SmallButton type="button" style={{ marginTop: 14 }} onClick={() => onOpenExplorer(Number(explorerTape.id))}>
      Open this period’s Collateral Explorer — loan details, eligibility and movements
    </SmallButton>}

    <Signoff open={historyOpen} onToggle={e => setHistoryOpen(e.currentTarget.open)} style={{ marginTop: 14 }}>
      <summary>Record <small>{plural(period.history.tapes.length, 'tape')} · {plural(period.history.certificates.length, 'certificate')} · {plural(period.history.runs.length, 'run')} · {plural(period.history.comparisons.length, 'comparison')}</small></summary>
      <Readable><HistoryTable facilityId={facilityId} history={period.history} currency={currency} currentTapeId={period.tape?.id ?? null} onWorkOnTape={onWorkOnTape} /></Readable>
    </Signoff>
  </>;
};

// ----------------------------------------------------------------------------

/** Where the tape came from: the operator's hand, a dropped file, or a connector that delivered it (F-S2-UI-003). */
const tapeSourceLabel = (t: { entryChannel?: string | null; fileName?: string | null }) => {
  const channel = String(t.entryChannel || '').toUpperCase();
  if (channel === 'GRID' || channel === 'GRID_ENTRY') return 'Entered by grid';
  if (channel === 'S3') return `${t.fileName || 'File'} · via S3`;
  if (channel === 'SNOWFLAKE') return `${t.fileName || 'Delivery'} · via Snowflake`;
  return t.fileName || 'File';
};

const TapeCard: React.FC<Pick<PaneProps, 'period' | 'facilityId' | 'currency' | 'busy' | 'run'>> = ({ period, facilityId, currency, busy, run }) => {
  const t = period.tape;
  const [mode, setMode] = useState<'file' | 'grid' | 'paste'>('file');
  const [replacing, setReplacing] = useState(false);
  const [paste, setPaste] = useState('');
  const [dragging, setDragging] = useState(false);
  const [asOf, setAsOf] = useState(period.periodEnd);
  const [rows, setRows] = useState<Array<Record<string, string>>>(() => Array.from({ length: 3 }, () => ({})));
  const [releaseNotes, setReleaseNotes] = useState('');
  const [scopeConfirmed, setScopeConfirmed] = useState(false);
  const [pendingFile, setPendingFile] = useState<{ file: File; channel: 'UI_UPLOAD' | 'GRID_ENTRY' } | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [discardReason, setDiscardReason] = useState('');
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [rowsOpen, setRowsOpen] = useState(false);
  const canRelease = period.readiness.nextAction.code === 'RELEASE_TAPE';
  useEffect(() => { setScopeConfirmed(false); setReleaseNotes(''); }, [t?.id, t?.asOfDate, period.id]);

  const submitFile = () => pendingFile && run('tape', () => addPeriodTape(facilityId, period.id, pendingFile.file, asOf, pendingFile.channel), `Tape received for ${period.label}`).then(ok => { if (ok) { setReplacing(false); setPendingFile(null); } });
  const submitGrid = () => {
    const csv = gridToCsv(rows);
    const file = new File([csv], `grid-entry-${period.label}-${asOf}.csv`, { type: 'text/csv' });
    setPendingFile({ file, channel: 'GRID_ENTRY' });
  };
  const visibleColumns = GRID_COLUMNS;
  const mayMake = period.readiness.actorMayMake;
  // F-MK-082: an unreleased candidate (a wrong file, a test upload) can be taken out of consideration.
  const mayDiscard = !!t && !['SUPERSEDED', 'DISCARDED'].includes(t.authorityStatus) && (mayMake || period.readiness.actorMayCheck);
  const submitDiscard = () => t && run('discard', () => discardPeriodTape(facilityId, t.id, discardReason.trim()),
    'Delivery discarded; it stays on record and can no longer be released').then(ok => { if (ok) { setDiscarding(false); setDiscardReason(''); } });

  return <Card>
    <CardHead>
      <div><h2>Servicer tape</h2><p>{t ? `${tapeSourceLabel(t)} · as of ${date(t.asOfDate)}` : 'The period’s loan tape — a file, or a small pool entered by hand.'}</p></div>
      {t && <StatusChip tone={t.authorityStatus === 'RELEASED' ? 'done' : t.authorityStatus === 'QUARANTINED' ? 'warning' : 'progress'} label={t.authorityStatus === 'RELEASED' ? 'Released' : t.authorityStatus === 'QUARANTINED' ? 'Needs attention' : t.authorityStatus === 'READY' ? 'Ready to release' : t.status === 'AWAITING_MAPPING' ? 'Held for mapping review' : t.authorityStatus} />}
    </CardHead>

    {t && !replacing ? <>
      <Facts>
        <dt>Collateral</dt><dd>{money(t.collateralBalance, currency)}{t.excludedBalance ? ` · excluded ${money(t.excludedBalance, currency)}` : ''}</dd>
        <dt>Eligible</dt><dd>{money(t.eligibleBalance, currency)}</dd>
        <dt>Ineligible</dt><dd>{money(t.ineligibleBalance, currency)}</dd>
        <dt>Rows</dt><dd>{t.status === 'AWAITING_MAPPING' ? `${t.rowsReceived} received — read once the column mapping is approved`
          : <>{t.rowsInserted} loaded{t.rowsFailed + t.rowsSkipped > 0 ? `, ${t.rowsFailed + t.rowsSkipped} need attention` : ''}{t.revisionCount ? ` · ${t.revisionCount} revised` : ''}</>}</dd>
        <dt>Received</dt><dd>{date(t.createdAt)} by {who(t.createdBy)}</dd>
        {t.releasedBy && <><dt>Released</dt><dd>{date(t.releasedAt)} by {t.releasedBy}{t.releaseNotes ? ` — “${t.releaseNotes}”` : ''}</dd></>}
        {t.mappingStatus && <><dt>Mapping</dt><dd>{t.mappingStatus === 'RECOGNISED' ? 'Recognised format' : t.mappingStatus === 'APPROVED' ? 'Approved mapping' : 'Unfamiliar columns — review below'}</dd></>}
      </Facts>
      <Signoff open={metricsOpen} onToggle={e => setMetricsOpen(e.currentTarget.open)} style={{ marginTop: 12 }}>
        <summary>Pool metrics <small>derived from the loan rows, with coverage</small></summary>
        <Readable><MetricsTable metrics={t.derivedMetrics} /></Readable>
      </Signoff>
      <Signoff open={rowsOpen} onToggle={e => setRowsOpen(e.currentTarget.open)} style={{ marginTop: 12 }}>
        <summary>Review or correct loan rows</summary>
        {rowsOpen && <TapeCorrectionPanel key={t.id} facilityId={facilityId} tapeId={t.id} mayEdit={mayMake} busy={busy !== null}
          onSave={action => run('row-correction', action, 'Recorded; an exclusion awaits a checker, and the revised tape requires release')} />}
      </Signoff>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        {mayMake && <SmallButton type="button" disabled={busy !== null} onClick={() => setReplacing(true)}>Add a replacement tape</SmallButton>}
        {mayDiscard && !discarding && <SmallButton type="button" disabled={busy !== null} onClick={() => setDiscarding(true)}>{t.authorityStatus === 'RELEASED' ? 'Withdraw unused release' : 'Discard this delivery'}</SmallButton>}
      </div>
      {mayDiscard && discarding && <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
        <FieldLabel htmlFor={`discard-reason-${t.id}`}>Why discard this delivery</FieldLabel>
        <Input id={`discard-reason-${t.id}`} value={discardReason} onChange={e => setDiscardReason(e.target.value)} placeholder="For example: wrong file — a test upload, or the servicer withdrew it" />
        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" $variant="danger" disabled={!discardReason.trim() || busy !== null} onClick={() => void submitDiscard()}>
            {busy === 'discard' ? 'Recording…' : t.authorityStatus === 'RELEASED' ? 'Withdraw this release' : 'Discard the delivery'}</Button>
          <SmallButton type="button" disabled={busy !== null} onClick={() => { setDiscarding(false); setDiscardReason(''); }}>Keep it</SmallButton>
        </div>
        <p style={{ color: INK.faint, fontSize: 12 }}>The original file, release and reason stay on record. Withdrawal is allowed only before downstream use; dependent draft calculations are rejected. Approved or used evidence requires a governed correction. Import the source again under its correct period.</p>
      </div>}
      {canRelease && <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
        <Note><p>Releasing <strong>{t.fileName || 'this tape'}</strong> into <strong>{period.label}</strong>, as of <strong>{date(t.asOfDate)}</strong>.</p><p>{t.evidenceScope?.message || 'Verify the reporting period against the original source before release.'}</p></Note>
        <label style={{ display: 'flex', gap: 8 }}><input type="checkbox" checked={scopeConfirmed} onChange={e => setScopeConfirmed(e.target.checked)} disabled={busy !== null} />I verified the source facility, period {period.label}, and tape date {date(t.asOfDate)}.</label>
        <FieldLabel htmlFor={`release-notes-${t.id}`}>Your release notes — what you checked</FieldLabel>
        <Input id={`release-notes-${t.id}`} value={releaseNotes} onChange={e => setReleaseNotes(e.target.value)} placeholder="Describe the evidence you reviewed and any exceptions you resolved" />
        <div><Button type="button" $variant="primary" disabled={!period.readiness.nextAction.enabled || !releaseNotes.trim() || !scopeConfirmed || busy !== null}
          onClick={() => run('release', () => releasePeriodTape(facilityId, t.id, releaseNotes, period.id, t.asOfDate), 'Tape released; computing the borrowing base')}>
          {busy === 'release' ? 'Releasing…' : 'Release the tape'}</Button></div>
        <p style={{ color: INK.faint, fontSize: 12 }}>Release makes this tape the period’s collateral authority, supersedes any earlier release for the period, and computes the base and the comparison automatically.</p>
      </div>}
      {t.authorityStatus !== 'RELEASED' && period.readiness.nextAction.code !== 'RELEASE_TAPE' && t.errorSummary && <Note style={{ marginTop: 12 }}><p>{t.errorSummary}</p></Note>}
    </> : <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <SmallButton type="button" onClick={() => setMode('file')} aria-pressed={mode === 'file'} style={mode === 'file' ? { borderColor: INK.accent } : undefined}><FileUp size={14} /> File</SmallButton>
        <SmallButton type="button" disabled={!mayMake || busy !== null} onClick={() => setMode('grid')} aria-pressed={mode === 'grid'} style={mode === 'grid' ? { borderColor: INK.accent } : undefined}><Keyboard size={14} /> Enter by hand</SmallButton>
        <SmallButton type="button" disabled={!mayMake || busy !== null} onClick={() => setMode('paste')} aria-pressed={mode === 'paste'}>Paste rows</SmallButton>
        {replacing && <SmallButton type="button" onClick={() => { setReplacing(false); setPendingFile(null); }}>Cancel replacement</SmallButton>}
        <FieldLabel style={{ marginLeft: 'auto', flexDirection: 'row', alignItems: 'center', gap: 8 }}>As of<DateField compact aria-label="As of" value={asOf} onChange={setAsOf} disabled={busy !== null} style={{ width: 150 }} /></FieldLabel>
      </div>
      {pendingFile && <TapeFileReview key={`${period.id}:${asOf}:${pendingFile.file.name}`} facilityId={facilityId} periodId={period.id} periodLabel={period.label} asOf={asOf} file={pendingFile.file} busy={busy !== null} onConfirm={() => void submitFile()} onCancel={() => setPendingFile(null)} />}
      {!pendingFile && (mode === 'file' ? <DropZone $active={dragging}
        onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f && mayMake && busy === null) setPendingFile({ file: f, channel: 'UI_UPLOAD' }); }}>
        <Upload size={22} />
        <strong>Drop the servicer tape here, or click to choose</strong>
        <small>CSV or XLSX. Recognised formats need no mapping step.</small>
        <input type="file" disabled={!mayMake || busy !== null} accept=".csv,.xlsx,text/csv" onChange={e => { const f = e.target.files?.[0]; if (f && mayMake && busy === null) setPendingFile({ file: f, channel: 'UI_UPLOAD' }); }} />
      </DropZone> : mode === 'paste' ? <>
        <FieldLabel>Paste a CSV or spreadsheet selection, including the header row
          <textarea rows={7} value={paste} onChange={e => setPaste(e.target.value)} aria-label="Pasted loan tape" />
        </FieldLabel>
        <Button type="button" $variant="primary" disabled={!mayMake || busy !== null || !paste.trim()}
          onClick={() => setPendingFile({ file: new File([paste], `pasted-${asOf}.csv`, { type: 'text/csv' }), channel: 'GRID_ENTRY' })}>Review pasted tape</Button>
      </> : <>
        <GridTable>
          <Table>
            <thead><tr>{visibleColumns.map(c => <th key={c.key}>{c.label}{c.required ? ' *' : ''}</th>)}</tr></thead>
            <tbody>{rows.map((row, i) => <tr key={i}>{visibleColumns.map(c => <td key={c.key}>
              <input value={row[c.key] || ''} inputMode={c.kind === 'number' ? 'decimal' : undefined} placeholder={c.kind === 'date' ? 'YYYY-MM-DD' : c.kind === 'flag' ? 'Y/N' : ''}
                onChange={e => setRows(prev => prev.map((r, j) => j === i ? { ...r, [c.key]: e.target.value } : r))} aria-label={`${c.label} row ${i + 1}`} />
            </td>)}</tr>)}</tbody>
          </Table>
        </GridTable>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
          <SmallButton type="button" onClick={() => setRows(prev => [...prev, {}])}>Add a row</SmallButton>
          <Button type="button" $variant="primary" disabled={!mayMake || busy !== null || !rows.some(r => (r.asset_identifier || '').trim() && (r.current_balance || '').trim())} onClick={() => void submitGrid()}>
            {busy === 'tape' ? 'Submitting…' : 'Review the tape'}</Button>
          <span style={{ color: INK.faint, fontSize: 12 }}>All rows are checked before a checker can release the tape.</span>
        </div>
      </>)}
    </>}
  </Card>;
};

const MetricsTable: React.FC<{ metrics: Record<string, PeriodMetric | number | string | null> }> = ({ metrics }) => {
  const rows: Array<[string, string]> = [
    ['delinquency30Pct', '30+ days delinquent'], ['delinquency60Pct', '60+ days delinquent'], ['delinquency90Pct', '90+ days delinquent'],
    ['defaultRatePct', 'Defaulted'], ['netLossRatePct', 'Net loss (period)'], ['prepaymentRatePct', 'Prepayments (period)'],
    ['weightedAverageYieldPct', 'Weighted-average yield'], ['weightedAverageLifeYears', 'Weighted-average life (years)'],
  ];
  return <Table>
    <thead><tr><th>Metric</th><th className="num">Computed</th><th className="num">Coverage</th><th>Note</th></tr></thead>
    <tbody>{rows.map(([key, label]) => {
      const m = metrics?.[key] as PeriodMetric | undefined;
      return <tr key={key}><td>{label}</td>
        <td className="num">{m?.status === 'COMPUTED' ? `${num(m.value, key.endsWith('Years') ? 2 : 2)}${key.endsWith('Pct') ? '%' : ''}` : <span style={{ color: '#f0c77a' }}>Unavailable</span>}</td>
        <td className="num">{m?.coveragePct != null ? `${num(m.coveragePct, 1)}%` : '—'}</td>
        <td style={{ color: INK.muted }}>{m?.reason || (m?.status === 'COMPUTED' ? 'From loan rows' : '')}</td></tr>;
    })}</tbody>
  </Table>;
};

const CertificateCard: React.FC<Pick<PaneProps, 'period' | 'facilityId' | 'currency' | 'busy' | 'run'>> = ({ period, facilityId, currency, busy, run }) => {
  const c = period.certificate;
  const [form, setForm] = useState({ asOfDate: period.periodEnd, sourceReference: '', borrowingBase: '', eligibleAmount: '', ineligibleAmount: '', reserveAmount: '', notes: '' });
  const [replacing, setReplacing] = useState(false);
  const submit = () => run('certificate', () => addKeyedCertificate(facilityId, {
    asOfDate: form.asOfDate, sourceReference: form.sourceReference.trim(),
    borrowingBase: parseAmountShorthand(form.borrowingBase),
    eligibleAmount: form.eligibleAmount ? parseAmountShorthand(form.eligibleAmount) : null,
    ineligibleAmount: form.ineligibleAmount ? parseAmountShorthand(form.ineligibleAmount) : null,
    reserveAmount: form.reserveAmount ? parseAmountShorthand(form.reserveAmount) : null,
    notes: form.notes,
  }), 'Certificate recorded; comparing when the base is ready').then(ok => { if (ok) setReplacing(false); });
  const showForm = !c || replacing;
  return <Card>
    <CardHead>
      <div><h2>Borrowing base certificate</h2><p>{c ? `${c.sourceReference} · as of ${date(c.asOfDate)} · ${c.entryChannel === 'KEYED' ? 'keyed' : 'from the document'}` : 'The borrower’s claim about the borrowing base — enter its figures here, or upload the PDF in Middle Office and it lands here once booked. The covenant figures are a different document, on Monitoring.'}</p></div>
      {c && <StatusChip tone="done" label="On record" />}
    </CardHead>
    {c && !replacing && <>
      <Facts>
        <dt>Collateral</dt><dd>{c.reportedEligible != null && c.reportedIneligible != null ? `${money(c.reportedEligible + c.reportedIneligible, currency)} (eligible + ineligible, as reported)` : 'Not reported'}</dd>
        <dt>Eligible</dt><dd>{money(c.reportedEligible, currency)}</dd>
        <dt>Ineligible</dt><dd>{money(c.reportedIneligible, currency)}</dd>
        <dt>Reserve</dt><dd>{money(c.reportedReserve, currency)}</dd>
        <dt>Reported base</dt><dd>{money(c.reportedBorrowingBase, currency)}</dd>
        <dt>Recorded</dt><dd>{date(c.createdAt)} by {who(c.createdBy)}{c.supersedesId ? ` · replaces #${c.supersedesId}` : ''}</dd>
      </Facts>
      {period.readiness.actorMayMake && <div style={{ marginTop: 10 }}><SmallButton type="button" onClick={() => setReplacing(true)}>Record a corrected certificate</SmallButton></div>}
    </>}
    {/* F-S2-UI-004: documents are ingested in one place — Middle Office → Document Processing.
        The booked certificate resolves its period from its as-of date and appears here. */}
    {showForm && <Note style={{ marginBottom: 10 }}>
      <p><strong>Have the PDF?</strong> <Link to="/middle-office?tab=document-processing">Upload it in Middle Office → Document Processing</Link>. The extraction is reviewed there and the certificate lands in this period automatically.</p>
    </Note>}
    {showForm && <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
      <FieldLabel>As of<DateField value={form.asOfDate} onChange={next => setForm({ ...form, asOfDate: next })} /></FieldLabel>
      <FieldLabel>Certificate reference<Input value={form.sourceReference} placeholder="BBC 2026-09, signed 2026-10-12" onChange={e => setForm({ ...form, sourceReference: e.target.value })} /></FieldLabel>
      <FieldLabel>Reported borrowing base<Input inputMode="decimal" placeholder="13.24MM or 13,242,450" value={form.borrowingBase} onChange={e => setForm({ ...form, borrowingBase: e.target.value })} onBlur={e => setForm({ ...form, borrowingBase: replaceShorthandOnBlur(e.target.value) })} /></FieldLabel>
      <FieldLabel>Reported eligible<Input inputMode="decimal" placeholder="18.9MM" value={form.eligibleAmount} onChange={e => setForm({ ...form, eligibleAmount: e.target.value })} onBlur={e => setForm({ ...form, eligibleAmount: replaceShorthandOnBlur(e.target.value) })} /></FieldLabel>
      <FieldLabel>Reported ineligible<Input inputMode="decimal" placeholder="436.5M" value={form.ineligibleAmount} onChange={e => setForm({ ...form, ineligibleAmount: e.target.value })} onBlur={e => setForm({ ...form, ineligibleAmount: replaceShorthandOnBlur(e.target.value) })} /></FieldLabel>
      <FieldLabel>Reported reserve<Input inputMode="decimal" placeholder="420M" value={form.reserveAmount} onChange={e => setForm({ ...form, reserveAmount: e.target.value })} onBlur={e => setForm({ ...form, reserveAmount: replaceShorthandOnBlur(e.target.value) })} /></FieldLabel>
      <FieldLabel style={{ gridColumn: '1 / -1' }}>Notes<Input value={form.notes} placeholder="Anything the certificate says that the numbers do not" onChange={e => setForm({ ...form, notes: e.target.value })} /></FieldLabel>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
        <Button type="button" $variant="primary" disabled={!period.readiness.actorMayMake || busy !== null || !form.sourceReference.trim() || !isValidAmountShorthand(form.borrowingBase)} onClick={() => void submit()}>{busy === 'certificate' ? 'Recording…' : 'Record the certificate'}</Button>
        {replacing && <SmallButton type="button" onClick={() => setReplacing(false)}>Cancel</SmallButton>}
      </div>
    </div>}
  </Card>;
};

const AttentionList: React.FC<Pick<PaneProps, 'period' | 'facilityId' | 'currency' | 'mappingReviews' | 'busy' | 'run'>> = ({ period, facilityId, currency, mappingReviews, busy, run }) => {
  const rowItems = period.attention.filter(a => a.action === 'REPAIR_ROWS' || a.action === 'DECIDE_EXCLUSIONS');
  const other = period.attention.filter(a => !rowItems.includes(a));
  return <section aria-label="Needs attention" style={{ marginBottom: 14 }}>
    <h3 style={{ color: INK.strong }}>Needs attention</h3>
    {rowItems.length > 0 && period.tape && <RowRepair period={period} facilityId={facilityId} currency={currency} busy={busy} run={run} />}
    {other.map(a => <AttentionCardView key={a.key} item={a} facilityId={facilityId} tapeId={period.tape?.id} busy={busy} run={run}
      mayCheck={period.readiness.actorMayCheck} mayMake={period.readiness.actorMayMake}
      profile={a.action === 'REVIEW_MAPPING' ? mappingReviews.find(m => m.id === a.ref) : undefined} />)}
  </section>;
};

/** The maker's proposal as select values ('' = not needed). */
const proposedChoices = (profile?: MappingReview): Record<string, string> =>
  Object.fromEntries(Object.entries(profile?.proposal?.columns || {}).map(([col, field]) => [col, field || '']));

const AttentionCardView: React.FC<{ item: AttentionItem; facilityId: number; tapeId?: number | null; busy: string | null; run: PaneProps['run']; profile?: MappingReview; mayCheck: boolean; mayMake?: boolean }> = ({ item, facilityId, tapeId, busy, run, profile, mayCheck, mayMake }) => {
  const loanIds = Array.isArray(item.evidence?.assetIdentifiers) ? (item.evidence!.assetIdentifiers as string[]) : [];
  const explorerFor = (loanId: string) => `${facilityWorkspacePath(facilityId, 'collateral')}?collateralView=explorer${tapeId ? `&tape=${tapeId}` : ''}&poolSearch=${encodeURIComponent(loanId)}`;
  const [notes, setNotes] = useState('');
  // F-MK-062: the checker sees the maker's proposal pre-filled and may approve it or change it.
  const [columnMap, setColumnMap] = useState<Record<string, string>>(() => proposedChoices(profile));
  const proposalStamp = profile?.proposal?.proposedAt;
  useEffect(() => { setColumnMap(proposedChoices(profile)); }, [proposalStamp]); // eslint-disable-line react-hooks/exhaustive-deps
  // Every unfamiliar column is sent, so "not needed" is an explicit choice, not a missing one.
  const chosen = Object.fromEntries((profile?.unmappedColumns || []).map(col => [col, columnMap[col] || '']));
  const mayApprove = mayCheck && profile?.mayApprove !== false;
  return <Attention $severity={item.severity}>
    <div className="head">
      <div><strong>{item.title}</strong><p>{item.detail}</p></div>
      <StatusChip tone={item.severity === 'HIGH' ? 'critical' : item.severity === 'MEDIUM' ? 'warning' : 'neutral'} label={item.severity.toLowerCase()} />
    </div>
    {item.action === 'REVIEW_MAPPING' && item.ref && <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
      {profile && profile.unmappedColumns.length > 0 && <Table>
        <thead><tr><th>Column in the delivery</th><th>Read it as</th></tr></thead>
        <tbody>{profile.unmappedColumns.map(col => <tr key={col}>
          <td>{col}</td>
          <td><select value={columnMap[col] || ''} onChange={e => setColumnMap({ ...columnMap, [col]: e.target.value })} aria-label={`Field for column ${col}`}
            style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${INK.lineStrong}`, background: INK.well, color: INK.body, minWidth: 220 }}>
            <option value="">Not needed — leave unread</option>
            {GRID_COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select></td>
        </tr>)}</tbody>
      </Table>}
      {profile?.proposal && <p style={{ color: INK.muted, fontSize: 13, margin: 0 }}>
        Proposed by {profile.proposal.proposedBy} on {date(profile.proposal.proposedAt)}{profile.proposal.notes ? ` — “${profile.proposal.notes}”` : ''}. Check each column; change any you disagree with, then approve.
      </p>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input value={notes} placeholder="Your notes — what you checked" onChange={e => setNotes(e.target.value)} style={{ flex: '1 1 260px' }} aria-label="Mapping review notes" />
        {(mayMake || mayCheck) && <SmallButton type="button" disabled={busy !== null || !profile}
          onClick={() => run('mapping-proposal', () => proposeMapping(facilityId, item.ref!, chosen, notes), 'Proposal saved; a checker approves it or changes it')}>
          {profile?.proposal ? 'Update the proposal' : 'Propose this mapping'}</SmallButton>}
        <Button type="button" $variant="primary" disabled={!mayApprove || busy !== null || !notes.trim()}
          onClick={() => run('mapping', () => approveMapping(facilityId, item.ref!, chosen, notes), 'Mapping approved; the delivery was re-read with it')}>Approve the mapping</Button>
      </div>
      {mayCheck && profile?.mayApprove === false && profile.whyNotApprove && <p style={{ color: INK.faint, fontSize: 12, margin: 0 }}>{profile.whyNotApprove} Propose the mapping so a checker sees your choices.</p>}
      <p style={{ color: INK.faint, fontSize: 12, margin: 0 }}>Approving records this format for the facility: later deliveries with the same columns are recognised without review, and this tape is re-read from the stored original under the approved mapping.</p>
    </div>}
    {item.action === 'RESOLVE_EXCEPTION' && <Link to={facilityWorkspacePath(facilityId, 'monitoring')}>Review the operational exception</Link>}
    {item.action === 'RESOLVE_FRAUD' && item.ref && <div style={{ marginTop: 10 }}>
      {loanIds.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }} aria-label="Loans in this finding">
        {loanIds.map(id => <Link key={id} to={explorerFor(id)} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: `1px solid ${INK.lineStrong}`, textDecoration: 'none' }} title="Open this loan in Collateral Explorer">{id}</Link>)}
      </div>}
      {item.evidence?.baselineTapeId != null && <p style={{ margin: '0 0 8px', color: INK.faint, fontSize: 12 }}>Compared against tape {String(item.evidence.baselineTapeId)}.</p>}
      <FieldLabel>Disposition and supporting evidence<Input value={notes} placeholder="What you checked and what you concluded" onChange={e => setNotes(e.target.value)} /></FieldLabel>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <SmallButton disabled={!mayCheck || busy !== null || !notes.trim()} onClick={() => run('fraud', () => decidePeriodFraud(facilityId, item.ref!, 'INVESTIGATE', notes), 'Investigation recorded')}>Investigate</SmallButton>
        <SmallButton disabled={!mayCheck || busy !== null || !notes.trim()} onClick={() => run('fraud', () => decidePeriodFraud(facilityId, item.ref!, 'DISMISS', notes), 'Finding dismissed with evidence')}>Dismiss</SmallButton>
        <SmallButton disabled={!mayCheck || busy !== null || !notes.trim()} onClick={() => run('fraud', () => decidePeriodFraud(facilityId, item.ref!, 'RESOLVE', notes), 'Finding resolved with evidence')}>Resolve</SmallButton>
      </div>
    </div>}
    {item.action === 'RESOLVE_VARIANCE'  && <p style={{ marginTop: 8, color: INK.faint, fontSize: 12 }}>Decide each line in the certificate comparison below.</p>}
    {item.action === 'RECOMPUTE' && item.ref && <SmallButton disabled={busy !== null} onClick={() => run('recompute', () => preparePeriodRecomputation(facilityId, item.ref!), 'A tape revision is ready for independent release')}>Prepare a replacement review</SmallButton>}
    {item.action === 'REPARSE' && item.ref && <SmallButton disabled={!mayCheck || busy !== null} onClick={() => run('reparse', () => reparsePeriodTape(facilityId, item.ref!), 'Stored delivery re-read under the approved mapping')}>Re-read stored delivery</SmallButton>}
  </Attention>;
};

const RowRepair: React.FC<Pick<PaneProps, 'period' | 'facilityId' | 'currency' | 'busy' | 'run'>> = ({ period, facilityId, currency, busy, run }) => {
  const tapeId = period.tape!.id;
  const [rows, setRows] = useState<RowIssue[] | null>(null);
  const [edits, setEdits] = useState<Record<number, Record<string, string>>>({});
  const [reasons, setReasons] = useState<Record<number, string>>({});
  const [evidence, setEvidence] = useState<Record<number, string>>({});
  const [rowError, setRowError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try { setRows(await getTapeRows(facilityId, tapeId)); setRowError(null); }
    catch (failure) { setRowError(apiErrorMessage(failure, 'Row evidence could not be loaded.')); }
  }, [facilityId, tapeId]);
  useEffect(() => { void reload(); }, [reload, period.tape?.rowsFailed, period.tape?.revisionCount]);
  if (rowError) return <Note role="alert">{rowError} <SmallButton onClick={() => void reload()}>Retry</SmallButton></Note>;
  if (!rows) return <Attention $severity="MEDIUM"><div className="head"><div><strong>Loading the rows that need attention…</strong></div></div></Attention>;
  const payload = (r: RowIssue) => (typeof r.raw_payload === 'object' && r.raw_payload ? r.raw_payload as Record<string, unknown> : {});
  const pendingProposal = (r: RowIssue) => (Array.isArray(r.revisions) ? r.revisions as Array<Record<string, unknown>> : []).find(v => v.kind === 'EXCLUSION' && v.checker_status === 'PENDING');
  const pendingExclusion = (r: RowIssue) => Boolean(pendingProposal(r));
  const openRows = rows.filter(r => r.status === 'OPEN' || pendingExclusion(r));
  if (!openRows.length) return null;
  const failing = openRows.filter(r => !pendingExclusion(r)).length;
  const proposed = openRows.length - failing;
  const title = [failing ? `${failing} row${failing === 1 ? '' : 's'} failed validation` : '', proposed ? `${proposed} exclusion${proposed === 1 ? '' : 's'} proposed` : ''].filter(Boolean).join(' · ');
  return <Attention $severity="MEDIUM">
    <div className="head"><div><strong>{title}</strong><p>{failing ? 'Fix the value where it is, with a reason and the evidence you used — or propose excluding the row for a checker to decide. The original stays on record.' : 'A checker other than the proposer decides each exclusion. The loan stays on the tape until approved.'}</p></div></div>
    {openRows.map(r => {
      const p = payload(r);
      const keys = Array.from(new Set([...Object.keys(p), 'asset_identifier', 'current_balance']));
      const proposal = pendingProposal(r);
      const pending = Boolean(proposal);
      return <div key={r.id} style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${INK.line}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <strong>Row {r.raw_row_number}{r.asset_identifier ? ` · ${r.asset_identifier}` : ''}</strong>
          {!pending && <span style={{ color: '#f0c77a', fontSize: 13 }}>{r.reason}</span>}
          {pending && <span style={{ color: '#f0c77a', fontSize: 13 }}>Exclusion proposed{proposal?.excluded_balance != null ? ` · ${money(Number(proposal.excluded_balance), currency)} would leave the pool` : ''}</span>}
        </div>
        {pending && proposal && <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, border: `1px solid ${INK.lineStrong}`, background: INK.well, fontSize: 13, display: 'grid', gap: 4 }} aria-label="Exclusion proposal">
          <div><span style={{ color: INK.faint }}>Proposed by</span> {String(proposal.actor ?? '—')}{proposal.created_at ? ` · ${date(String(proposal.created_at))}` : ''}</div>
          <div><span style={{ color: INK.faint }}>Reason</span> {String(proposal.reason ?? '—')}</div>
          <div><span style={{ color: INK.faint }}>Evidence</span> {String(proposal.evidence_reference ?? '—')}</div>
        </div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginTop: 8 }}>
          {keys.map(k => <FieldLabel key={k}>{k}<Input value={edits[r.id]?.[k] ?? String(p[k] ?? '')} onChange={e => setEdits({ ...edits, [r.id]: { ...(edits[r.id] || {}), [k]: e.target.value } })} /></FieldLabel>)}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input value={reasons[r.id] || ''} aria-label="Correction or checker decision reason" placeholder={pending ? 'Your decision — what you checked' : 'Reason for correction or exclusion'} onChange={e => setReasons({ ...reasons, [r.id]: e.target.value })} style={{ flex: '1 1 320px' }} />
          {!pending && <Input aria-label="Supporting evidence reference" value={evidence[r.id] || ''} placeholder="Supporting evidence reference (e.g. servicer email 2026-10-14)" onChange={e => setEvidence({ ...evidence, [r.id]: e.target.value })} style={{ flex: '1 1 320px' }} />}
          {pending ? <>
            <span style={{ color: INK.muted, fontSize: 13 }}>Exclusion proposed — awaiting a checker</span>
            {period.readiness.actorMayCheck && <>
              <SmallButton type="button" disabled={busy !== null || !(reasons[r.id] || '').trim()} onClick={() => run(`excl-${r.id}`, () => decideRowExclusion(facilityId, tapeId, r.raw_row_number, true, reasons[r.id]), `Row ${r.raw_row_number} excluded`)}>Approve exclusion</SmallButton>
              <SmallButton type="button" disabled={busy !== null || !(reasons[r.id] || '').trim()} onClick={() => run(`excl-${r.id}`, () => decideRowExclusion(facilityId, tapeId, r.raw_row_number, false, reasons[r.id]), 'Exclusion rejected')}>Reject</SmallButton>
            </>}
          </> : <>
            <Button type="button" $variant="primary" disabled={!period.readiness.actorMayMake || busy !== null || !(reasons[r.id] || '').trim() || !(evidence[r.id] || '').trim() || !edits[r.id]}
              onClick={() => run(`fix-${r.id}`, () => correctTapeRow(facilityId, tapeId, r.raw_row_number, edits[r.id] || {}, reasons[r.id], evidence[r.id]), `Row ${r.raw_row_number} corrected and revalidated`)}>Save the correction</Button>
            <SmallButton type="button" disabled={!period.readiness.actorMayMake || busy !== null || !(reasons[r.id] || '').trim() || !(evidence[r.id] || '').trim()}
              onClick={() => run(`ex-${r.id}`, () => proposeRowExclusion(facilityId, tapeId, r.raw_row_number, reasons[r.id], evidence[r.id]), `Exclusion of row ${r.raw_row_number} proposed`)}>Propose exclusion{r.balance ? ` (${money(r.balance, currency)})` : ''}</SmallButton>
          </>}
        </div>
      </div>;
    })}
  </Attention>;
};

const HistoryTable: React.FC<{ facilityId: number; history: CollectionPeriod['history']; currency?: string | null; currentTapeId?: number | null; onWorkOnTape?: (tapeId: number | null) => void }> = ({ facilityId, history, currency, currentTapeId, onWorkOnTape }) => {
  const rows: Array<{ kind: string; id: unknown; when: unknown; who: unknown; what: string; state: string; action?: React.ReactNode }> = [];
  history.tapes.forEach(t => rows.push({ kind: 'Tape', id: t.id, when: t.created_at, who: t.created_by,
    what: `${t.source_filename || t.entry_channel || ''} · ${money(t.collateral_balance as number, currency)}`
      + (String(t.authority_status) === 'DISCARDED' ? ` · discarded by ${String(t.discarded_by || '—')}${t.discard_reason ? ` — “${String(t.discard_reason)}”` : ''}` : ''),
    state: String(t.authority_status),
    // R-07: any READY candidate in the period may be worked on and released; the newest is only the default.
    action: onWorkOnTape && String(t.authority_status) === 'READY' ? (Number(t.id) === currentTapeId
      ? <span style={{ color: INK.accentSoft, fontSize: 12 }}>Working on this tape</span>
      : <SmallButton type="button" onClick={() => onWorkOnTape(Number(t.id))}>Work on this tape</SmallButton>) : undefined }));
  history.certificates.forEach(c => rows.push({ kind: 'Certificate', id: c.id, when: c.created_at, who: c.created_by, what: `${c.source_reference} · ${money(c.reported_borrowing_base as number, currency)}`, state: c.superseded_by_submission_id ? 'SUPERSEDED' : String(c.status || 'CURRENT') }));
  history.runs.forEach(r => rows.push({ kind: 'Base', id: r.id, when: r.created_at, who: r.created_by, what: money(r.computed_borrowing_base as number, currency), state: String(r.approval_status) }));
  history.comparisons.forEach(c => rows.push({ kind: 'Comparison', id: c.id, when: c.created_at, who: c.created_by, what: String(c.variance_status || ''), state: String(c.status) }));
  if (!rows.length) return <p style={{ color: INK.muted }}>Nothing recorded for this period yet.</p>;
  return <>
  <Table>
    <thead><tr><th>What</th><th>Detail</th><th>By</th><th>When</th><th>State</th><th></th></tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i} data-accepted={/SUPERSEDED|REJECTED|DISCARDED/.test(r.state)}>
      <td>{r.kind} #{String(r.id)}</td><td>{r.what}</td><td>{String(r.who || '—')}</td><td>{date(String(r.when))}</td><td>{r.state.toLowerCase()}</td><td>{r.action ?? null}</td>
    </tr>)}</tbody>
  </Table>
  {history.comparisons.map(comparison => <ComparisonRecord key={String(comparison.id)} facilityId={facilityId} comparisonId={Number(comparison.id)} />)}
  </>;
};

const ComparisonRecord: React.FC<{ facilityId: number; comparisonId: number }> = ({ facilityId, comparisonId }) => {
  const [record, setRecord] = useState<Awaited<ReturnType<typeof getComparisonRecord>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = async () => { try { setRecord(await getComparisonRecord(facilityId, comparisonId)); setError(null); } catch (failure) { setError(apiErrorMessage(failure, 'Comparison evidence could not be loaded.')); } };
  return <details onToggle={e => { if (e.currentTarget.open && !record) void load(); }} style={{ marginTop: 10 }}>
    <summary>Read comparison #{comparisonId} and its decisions</summary>
    {error && <Note role="alert">{error} <SmallButton onClick={() => void load()}>Retry</SmallButton></Note>}
    {record && <Table><thead><tr><th>Line</th><th>Reported / computed</th><th>Recorded decision</th></tr></thead><tbody>
      {record.lines.map(line => <tr key={line.id}><td>{line.line_key}</td><td>{money(line.reported_value)} / {money(line.computed_value)}</td>
        <td>{line.disposition ? `${line.disposition.toLowerCase()} by ${line.disposed_by}: ${line.disposition_reason}` : 'No disposition recorded'}</td></tr>)}
    </tbody></Table>}
  </details>;
};

export default PeriodWorkspace;
